import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Dropdown,
  Empty,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Select,
  Space,
  Table,
  Tabs,
  Tag,
  Tooltip,
  Typography,
  message,
} from "antd";
import {
  AppstoreAddOutlined,
  ArrowDownOutlined,
  ArrowUpOutlined,
  BarcodeOutlined,
  DeleteOutlined,
  DisconnectOutlined,
  DownOutlined,
  EditOutlined,
  FlagOutlined,
  LinkOutlined,
  NumberOutlined,
  OrderedListOutlined,
  PlusOutlined,
  PrinterOutlined,
  ReloadOutlined,
  SaveOutlined,
  SearchOutlined,
  SplitCellsOutlined,
  ThunderboltOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useReactToPrint } from "react-to-print";
import Loading from "components/shared-components/Loading";
import Flex from "components/shared-components/Flex";
import ShelfLabelSheet, { SHELF_LABEL_PAGE_STYLE } from "components/shared-components/Print/ShelfLabelSheet";
import RackSignSheet from "components/shared-components/Print/RackSignSheet";
import { SHELF_LABEL_FORMATS } from "constants/PickingConstant";
import { usePickRoute, useProductLinks, useProducts } from "../../../../data/hooks";
import { buildArticleCatalogue } from "../../../../functions/articleCode";
import {
  compareLocationCodes,
  findLabelMismatches,
  findPackageEntries,
  findUnplacedProducts,
  itemWeight,
  moveEntry,
  nextLocationCode,
  normalizeProductName,
  pickableProducts,
  planOrphanCleanup,
  sortRouteByLocation,
  summariseRouteReadiness,
} from "../../../../functions/pickRoute";
import { buildRackSigns, buildShelfLabels, labelsForSheet } from "../../../../functions/shelfLabels";
import { SHOP_COLORS } from "../product-data/constants";
import FillVakkenModal from "./FillVakkenModal";
import FreezerMap from "./FreezerMap";
import LabelPrintModal from "./LabelPrintModal";
import LocationDrawer from "./LocationDrawer";
import NumberingModal from "./NumberingModal";
import OrphanCleanupModal from "./OrphanCleanupModal";
import RackSignModal from "./RackSignModal";
import WeightSplitModal from "./WeightSplitModal";

const { Text } = Typography;

const TAB_TABLE = "table";
const TAB_MAP = "map";

const itemKey = (item) => `${item.shopSource}|${item.productId}|${itemWeight(item) === null ? "*" : itemWeight(item)}`;

/** Dutch text for the refusals the hook returns (`{ ok: false, reason }`) or throws. */
const refusalMessage = (t, result) => {
  switch (result && result.reason) {
    case "pickRoute/location-taken":
      return t("pick_route.error_location_taken", {
        location: (result.takenBy && result.takenBy.location) || result.location || "",
        label: (result.takenBy && result.takenBy.label) || "",
      });
    case "pickRoute/invalid-location":
      return t("pick_route.location_invalid");
    case "pickRoute/split-first":
      return t("pick_route.error_split_first");
    case "pickRoute/already-split":
      return t("pick_route.error_already_split");
    case "pickRoute/has-location":
      return t("pick_route.error_has_location");
    case "pickRoute/not-found":
      return t("pick_route.error_not_found");
    case "pickRoute/not-empty":
      return t("pick_route.error_not_empty");
    default:
      return (result && result.reason) || t("pick_route.error_unknown");
  }
};

/**
 * Magazijn: vakken en looproute (HVC-46, HVC-43, HVC-47).
 *
 * One list, in the order the freezer is walked, where every entry is one vak: its code (1A,
 * 40H), the barcode on its label, the text on its bordje, and the products that lie in it - per
 * weight, because every weight has its own vak (planning/orderpicking-bouwplan-cms.md, besluit
 * 2). Both shops' packing slips are sorted along it, and every pick round is built from it.
 *
 * Reordering happens on a local working copy and is written in one go, so fine-tuning a route of
 * several hundred vakken does not turn into several hundred Firestore writes. Everything else -
 * a code, a split, a label - writes immediately, and is disabled while a reorder is unsaved.
 */
const PickRoute = () => {
  const { t } = useTranslation();
  const { products, isLoading: productsLoading, error: productsError, loadedShops } = useProducts();
  const {
    entries,
    isLoading: routeLoading,
    saveOrder,
    generateFromProducts,
    syncFromProducts,
    resetOrder,
    deleteEntry,
    linkProductToEntry,
    fillVak,
    detachProduct,
    cleanOrphans,
    createEntry,
    assignLocation,
    assignLocations,
    setLabels,
    splitByWeight,
  } = usePickRoute();
  const { links, isLoaded: linksLoaded } = useProductLinks();

  // Working copy: the route as it would be after the edits that are not saved yet
  const [draft, setDraft] = useState([]);
  const [isDirty, setIsDirty] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [zoneFilter, setZoneFilter] = useState(null);
  // Only the vakken whose bordje text names something else than what lies in them
  const [onlyLabelMismatch, setOnlyLabelMismatch] = useState(false);
  const [tab, setTab] = useState(TAB_TABLE);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  // The table's own sorting, on # or vak; the route itself keeps its order
  const [tableSort, setTableSort] = useState(null);
  const [isBusy, setIsBusy] = useState(false);
  const [linkTarget, setLinkTarget] = useState(null);
  const [itemToLink, setItemToLink] = useState(null);
  const [productSearch, setProductSearch] = useState("");
  const [drawer, setDrawer] = useState({ open: false, entryId: null, suggestedCode: "" });
  const [numberingOpen, setNumberingOpen] = useState(false);
  const [splitOpen, setSplitOpen] = useState(false);
  const [cleanupOpen, setCleanupOpen] = useState(false);
  const [fillOpen, setFillOpen] = useState(false);
  const [labelRequest, setLabelRequest] = useState(null);
  // The format of the last print: a single label from a row comes out the same way
  const [labelFormat, setLabelFormat] = useState("card");
  const [printJob, setPrintJob] = useState(null);
  const labelSheetRef = useRef(null);
  // Stellingbordjes: one A4 per stelling, for the head of the rack
  const [signsOpen, setSignsOpen] = useState(false);
  const [signJob, setSignJob] = useState(null);
  const signSheetRef = useRef(null);

  const isLoading = routeLoading || productsLoading;

  // Adopt the stored route whenever it changes, unless there is unsaved reordering to protect
  useEffect(() => {
    if (isDirty) return;
    setDraft(entries);
  }, [entries, isDirty]);

  const catalogue = useMemo(() => buildArticleCatalogue(products), [products]);
  const pickable = useMemo(() => pickableProducts(products), [products]);
  const unplacedProducts = useMemo(() => findUnplacedProducts(entries, pickable), [entries, pickable]);
  // Spots pointing at products that no longer exist - judged only for shops whose catalogue loaded
  const orphanPlan = useMemo(() => planOrphanCleanup(entries, products, { loadedShops }), [entries, products, loadedShops]);
  const orphanCount = orphanPlan.updates.length + orphanPlan.deletes.length;
  const notLoadedShops = ["VCE", "HVO"].filter((shop) => !!loadedShops && !loadedShops.includes(shop));
  const packageEntryIds = useMemo(() => new Set(findPackageEntries(entries, products).map((entry) => entry.id)), [entries, products]);
  const readiness = useMemo(() => summariseRouteReadiness({ entries, products, catalogue }), [entries, products, catalogue]);
  const zones = useMemo(() => [...new Set(entries.map((entry) => entry.zone).filter(Boolean))].sort(), [entries]);
  // Per vak the bordje text that fits what lies in it, for the vakken whose text says otherwise
  const labelFixes = useMemo(() => new Map(findLabelMismatches(entries).map(({ entry, expected }) => [entry.id, expected])), [entries]);

  const printLabels = useReactToPrint({
    contentRef: labelSheetRef,
    documentTitle: "Vaketiketten",
    pageStyle: SHELF_LABEL_PAGE_STYLE,
    onAfterPrint: () => setPrintJob(null),
  });

  // The sheet is rendered in the same commit that sets the job, so it exists when this runs; the
  // barcodes are drawn synchronously, so nothing is still being drawn when the print starts.
  useEffect(() => {
    if (printJob) printLabels();
  }, [printJob, printLabels]);

  const printSigns = useReactToPrint({
    contentRef: signSheetRef,
    documentTitle: "Stellingbordjes",
    pageStyle: SHELF_LABEL_PAGE_STYLE,
    onAfterPrint: () => setSignJob(null),
  });
  useEffect(() => {
    if (signJob) printSigns();
  }, [signJob, printSigns]);

  /**
   * Everything that can be put on the entry being edited, per article: one option per weight for
   * a product sold in weights, one for all weights otherwise. A product that still has an "all
   * weights" vak only offers that one - it has to be split per weight before a single weight can
   * be moved (besluit 2). Products sitting on another entry are offered too: linking is a move.
   */
  const linkableItems = useMemo(() => {
    if (!linkTarget) return [];
    const position = new Map();
    const withCatchAll = new Set();
    entries.forEach((entry, entryIndex) =>
      (entry.products || []).forEach((item) => {
        position.set(itemKey(item), { entry, index: entryIndex });
        if (itemWeight(item) === null) withCatchAll.add(`${item.shopSource}|${item.productId}`);
      }),
    );

    const options = [];
    catalogue.forEach((product) => {
      const weights = product.articles.map((article) => article.weight).filter((weight) => weight !== null);
      const variants = weights.length === 0 || withCatchAll.has(product.key) ? [null] : weights;
      variants.forEach((weight) => {
        const key = `${product.key}|${weight === null ? "*" : weight}`;
        const placedAt = position.get(key) || null;
        if (!!placedAt && placedAt.entry.id === linkTarget.id) return;
        options.push({
          key,
          weight,
          placedAt,
          allWeights: weight === null && weights.length > 0,
          product: { id: product.productId, shopSource: product.shopSource, name: product.name },
        });
      });
    });
    return options.sort((a, b) => {
      // Articles without a vak first - those are the ones most likely to be looked for
      if (!a.placedAt !== !b.placedAt) return a.placedAt ? 1 : -1;
      return (
        (a.product.name || "").localeCompare(b.product.name || "", "nl") ||
        String(a.weight || "").localeCompare(String(b.weight || ""), "nl", { numeric: true })
      );
    });
  }, [entries, catalogue, linkTarget]);

  const filteredDraft = useMemo(() => {
    const needle = normalizeProductName(searchText);
    return draft.filter((entry) => {
      if (!!zoneFilter && entry.zone !== zoneFilter) return false;
      if (onlyLabelMismatch && !labelFixes.has(entry.id)) return false;
      if (!needle) return true;
      const haystack = [entry.location, entry.label, ...(entry.products || []).map((product) => product.name)]
        .map(normalizeProductName)
        .join(" ");
      return haystack.includes(needle);
    });
  }, [draft, searchText, zoneFilter, onlyLabelMismatch, labelFixes]);

  // Reordering is relative to the whole route, so a filtered view cannot be dragged through.
  // Position numbers still work while searching - they address the full list directly.
  const isFiltered = filteredDraft.length !== draft.length;
  // The same for a view sorted on anything but the route order: an arrow would move a row past
  // neighbours that are not the ones on screen
  const isSorted = !!tableSort && !!tableSort.order && !(tableSort.columnKey === "sequence" && tableSort.order === "ascend");
  const positionOf = useMemo(() => new Map(draft.map((entry, index) => [entry.id, index])), [draft]);

  const applyMove = (entry, toIndex) => {
    const fromIndex = draft.findIndex((candidate) => candidate.id === entry.id);
    if (fromIndex === -1) return;
    setDraft(moveEntry(draft, fromIndex, toIndex));
    setIsDirty(true);
  };

  /** Busy flag, and one place that turns refusals and errors into a message. */
  /** A basket clicked on the map: its vak in the table, where everything can be done to it. */
  const showVakInTable = (code) => {
    setZoneFilter(null);
    setOnlyLabelMismatch(false);
    setSearchText(code);
    setTab(TAB_TABLE);
  };

  const fixLabels = (entryIds) =>
    runAction(
      () => setLabels(entryIds.filter((entryId) => labelFixes.has(entryId)).map((entryId) => ({ entryId, label: labelFixes.get(entryId) }))),
      (outcome) => t("pick_route.labels_fixed", { count: outcome.written }),
    );

  const runAction = async (action, successMessage) => {
    setIsBusy(true);
    try {
      const result = await action();
      if (result && result.ok === false) {
        message.error(refusalMessage(t, result), 6);
        return result;
      }
      if (successMessage) message.success(successMessage(result), 4);
      return result;
    } catch (error) {
      message.error(error.message && error.message.startsWith("pickRoute/") ? refusalMessage(t, { reason: error.message }) : error.message, 6);
      return { ok: false, reason: error.message };
    } finally {
      setIsBusy(false);
    }
  };

  const handleSaveOrder = () =>
    runAction(
      async () => {
        await saveOrder(draft);
        setIsDirty(false);
      },
      () => t("pick_route.order_saved"),
    );

  /** "Looproute op vakcode zetten": the code order as a new working copy, saved like a drag. */
  const applyLocationOrder = () => {
    const sorted = sortRouteByLocation(draft);
    if (sorted.every((entry, index) => entry.id === draft[index].id)) {
      message.info(t("pick_route.sort_by_location_nothing"), 4);
      return;
    }
    setDraft(sorted);
    setIsDirty(true);
    message.success(t("pick_route.sort_by_location_done"), 6);
  };

  const handleDiscard = () => {
    setDraft(entries);
    setIsDirty(false);
  };

  const handleGenerate = () =>
    runAction(
      () => generateFromProducts(pickable),
      (seeded) => t("pick_route.generated", { count: seeded.length }),
    );

  const handleSync = () =>
    runAction(
      () => syncFromProducts(pickable),
      (changes) =>
        t("pick_route.synced", {
          created: changes.created.length,
          attached: changes.attached.reduce((total, addition) => total + addition.products.length, 0),
        }),
    );

  const handleCleanup = () =>
    runAction(
      async () => {
        const plan = await cleanOrphans(products, { loadedShops });
        setCleanupOpen(false);
        return plan;
      },
      (plan) => t("pick_route.cleanup_done", { updated: plan.updates.length, deleted: plan.deletes.length }),
    );

  const handleReset = () =>
    Modal.confirm({
      title: t("pick_route.reset"),
      content: t("pick_route.confirm_reset"),
      okText: t("yes"),
      cancelText: t("no"),
      okButtonProps: { danger: true },
      onOk: () =>
        runAction(
          async () => {
            await resetOrder(pickable);
            setIsDirty(false);
          },
          () => t("pick_route.order_reset"),
        ),
    });

  // --- vakken ------------------------------------------------------------------------------

  const drawerEntry = drawer.entryId ? entries.find((entry) => entry.id === drawer.entryId) || null : null;

  /** The next vak in walking order that still has no code - where "opslaan en volgende" goes. */
  const nextWithoutCode = (afterId) => {
    const start = draft.findIndex((entry) => entry.id === afterId);
    return draft.slice(start + 1).find((entry) => !entry.location && !packageEntryIds.has(entry.id)) || null;
  };

  const openEditor = (entry) => setDrawer({ open: true, entryId: entry.id, suggestedCode: "" });
  const openCreator = () => {
    const codes = entries.map((entry) => entry.location).filter(Boolean);
    const lastCode = codes.length > 0 ? codes[codes.length - 1] : null;
    setDrawer({ open: true, entryId: null, suggestedCode: (lastCode && nextLocationCode(lastCode)) || "" });
  };
  const closeDrawer = () => setDrawer({ open: false, entryId: null, suggestedCode: "" });

  const handleSaveVak = async (values, { next }) => {
    setIsBusy(true);
    try {
      if (!drawer.entryId) {
        const result = await createEntry(values);
        if (!result.ok) return { ok: false, message: refusalMessage(t, result) };
        message.success(t("pick_route.vak_created", { location: values.location || values.label || "" }), 3);
        if (next) setDrawer({ open: true, entryId: null, suggestedCode: (values.location && nextLocationCode(values.location)) || "" });
        else closeDrawer();
        return result;
      }
      const result = await assignLocation(drawer.entryId, values);
      if (!result.ok) return { ok: false, message: refusalMessage(t, result) };
      message.success(t("pick_route.vak_saved", { location: values.location || "-" }), 2);
      const following = next ? nextWithoutCode(drawer.entryId) : null;
      if (following) setDrawer({ open: true, entryId: following.id, suggestedCode: "" });
      else closeDrawer();
      return result;
    } catch (error) {
      return { ok: false, message: error.message };
    } finally {
      setIsBusy(false);
    }
  };

  const handleNumbering = async (changes) => {
    const result = await runAction(
      () => assignLocations(changes),
      (outcome) => t("pick_route.numbering_done", { count: outcome.written }),
    );
    if (result && result.ok !== false) setNumberingOpen(false);
  };

  const handleSplit = async () => {
    const result = await runAction(
      () => splitByWeight(catalogue),
      (outcome) => t("pick_route.split_done", { entries: outcome.splitEntries, created: outcome.createdEntries }),
    );
    if (result && result.ok !== false) setSplitOpen(false);
  };

  const labelEntries = selectedRowKeys.length > 0 ? draft.filter((entry) => selectedRowKeys.includes(entry.id)) : draft;
  const labelPreview = useMemo(() => buildShelfLabels(labelEntries), [labelEntries]);
  // The stellingen of the chosen vakken, or all of them
  const signPreview = useMemo(() => buildRackSigns(labelEntries), [labelEntries]);

  /** Print these labels in a format; an empty vak has no product card, and says so instead of printing a blank sheet. */
  const printLabelsAs = (labels, format, startSlot = 1) => {
    const sheet = SHELF_LABEL_FORMATS[format];
    const printable = labelsForSheet(labels, sheet);
    if (printable.length === 0) {
      message.info(t("pick_route.print_product_empty"));
      return;
    }
    setPrintJob({ labels: printable, startSlot, sheet });
  };

  const startLabelPrint = ({ startSlot, format }) => {
    setLabelFormat(format);
    printLabelsAs(labelPreview.labels, format, startSlot);
    setLabelRequest(null);
  };

  // --- linking -----------------------------------------------------------------------------

  const openLinkModal = (entry) => {
    setLinkTarget(entry);
    setItemToLink(null);
    // Pre-search on the entry's own name: the product being looked for is almost always the
    // near-namesake from the other shop, so it should already be on screen
    setProductSearch(normalizeProductName(entry.label || "").split(" ").slice(0, 2).join(" "));
  };

  const closeLinkModal = () => {
    setLinkTarget(null);
    setItemToLink(null);
    setProductSearch("");
  };

  const handleLinkProduct = async () => {
    const match = linkableItems.find((candidate) => candidate.key === itemToLink);
    if (!match || !linkTarget) return;

    const result = await runAction(
      () => linkProductToEntry(linkTarget, match.product, { weight: match.weight }),
      (plan) => {
        const product = `${match.product.name}${match.weight ? ` ${match.weight} gram` : ""}`;
        if (!plan) return t("pick_route.product_already_linked");
        if (plan.removeSource) {
          return t("pick_route.product_moved_and_removed", { product, entry: linkTarget.label, removed: plan.source.label });
        }
        if (!!plan.source) return t("pick_route.product_moved", { product, entry: linkTarget.label, from: plan.source.label });
        return t("pick_route.product_linked", { product, entry: linkTarget.label });
      },
    );
    if (!result || result.ok !== false) closeLinkModal();
  };

  // --- table -------------------------------------------------------------------------------

  const columns = [
    {
      title: "#",
      key: "sequence",
      dataIndex: "sequence",
      width: 100,
      sorter: (a, b) => positionOf.get(a.id) - positionOf.get(b.id),
      render: (_, entry) => {
        const position = draft.findIndex((candidate) => candidate.id === entry.id);
        return (
          <InputNumber
            size="small"
            min={1}
            max={draft.length}
            value={position + 1}
            style={{ width: 72 }}
            // Committed on blur/enter rather than per keystroke, so typing "12" does not
            // first move the entry to position 1
            onPressEnter={(event) => applyMove(entry, Number(event.target.value) - 1)}
            onBlur={(event) => {
              const next = Number(event.target.value) - 1;
              if (!Number.isNaN(next) && next !== position) applyMove(entry, next);
            }}
          />
        );
      },
    },
    {
      title: "",
      dataIndex: "move",
      width: 76,
      render: (_, entry) => {
        const position = draft.findIndex((candidate) => candidate.id === entry.id);
        return (
          <Space size={4}>
            <Button size="small" icon={<ArrowUpOutlined />} disabled={position <= 0 || isFiltered || isSorted} onClick={() => applyMove(entry, position - 1)} />
            <Button
              size="small"
              icon={<ArrowDownOutlined />}
              disabled={position === -1 || position >= draft.length - 1 || isFiltered || isSorted}
              onClick={() => applyMove(entry, position + 1)}
            />
          </Space>
        );
      },
    },
    {
      title: t("pick_route.location"),
      key: "location",
      dataIndex: "location",
      width: 96,
      // In code order, a place without a code last either way: it is not a vak yet
      sorter: (a, b, order) => {
        if (!a.location || !b.location) return (!a.location - !b.location) * (order === "descend" ? -1 : 1);
        return compareLocationCodes(a.location, b.location);
      },
      render: (location, entry) => {
        if (packageEntryIds.has(entry.id)) {
          return (
            <Tooltip title={t("pick_route.package_entry_tooltip")}>
              <Tag>{t("pick_route.package_entry")}</Tag>
            </Tooltip>
          );
        }
        return location ? (
          <Button type="link" size="small" style={{ padding: 0, fontFamily: "monospace", fontWeight: 700, fontSize: 15 }} onClick={() => openEditor(entry)}>
            {location}
          </Button>
        ) : (
          <Button size="small" type="dashed" onClick={() => openEditor(entry)} disabled={isDirty}>
            {t("pick_route.set_location")}
          </Button>
        );
      },
    },
    {
      title: t("pick_route.sign_text"),
      dataIndex: "label",
      render: (label, entry) => (
        <div>
          <strong>{label || "-"}</strong>
          {!!entry.zone && (
            <div>
              <Tag style={{ marginTop: 2 }}>{entry.zone}</Tag>
            </div>
          )}
          {labelFixes.has(entry.id) && (
            <div style={{ marginTop: 4 }}>
              <Tooltip title={t("pick_route.label_mismatch_tooltip")}>
                <Tag color="warning" icon={<WarningOutlined />}>
                  {t("pick_route.label_mismatch", { label: labelFixes.get(entry.id) })}
                </Tag>
              </Tooltip>
              <Button size="small" type="link" style={{ padding: 0 }} disabled={isDirty || isBusy} onClick={() => fixLabels([entry.id])}>
                {t("pick_route.label_fix")}
              </Button>
            </div>
          )}
        </div>
      ),
    },
    {
      title: t("pick_route.linked_products"),
      dataIndex: "products",
      render: (entryProducts, entry) => {
        if (!entryProducts || entryProducts.length === 0) {
          return <Tag color={entry.location ? "orange" : "red"}>{entry.location ? t("pick_route.empty_vak") : t("pick_route.no_products")}</Tag>;
        }
        return (
          <Space size={4} wrap>
            {entryProducts.map((product) => {
              const weight = itemWeight(product);
              return (
                <Tooltip key={itemKey(product)} title={`${product.name || "-"} (${product.shopSource})${weight !== null ? ` · ${weight} gram` : ` · ${t("pick_route.all_weights")}`}`}>
                  <Tag
                    color={SHOP_COLORS[product.shopSource] || "default"}
                    closable={entryProducts.length > 1 || !!entry.location}
                    closeIcon={<DisconnectOutlined />}
                    onClose={(event) => {
                      event.preventDefault();
                      runAction(() => detachProduct(entry, product));
                    }}
                  >
                    {product.shopSource} · {product.name || product.productId}
                    {weight !== null ? ` · ${weight} g` : ""}
                  </Tag>
                </Tooltip>
              );
            })}
          </Space>
        );
      },
    },
    {
      title: "",
      dataIndex: "actions",
      width: 132,
      render: (_, entry) => (
        <Space size={4}>
          <Tooltip title={t("pick_route.edit_vak")}>
            <Button size="small" icon={<EditOutlined />} disabled={isDirty} onClick={() => openEditor(entry)} />
          </Tooltip>
          <Tooltip title={t("pick_route.link_product")}>
            <Button size="small" icon={<LinkOutlined />} onClick={() => openLinkModal(entry)} />
          </Tooltip>
          <Tooltip
            title={entry.location ? t("pick_route.print_one_label", { format: t(`pick_route.print_format_short_${labelFormat}`) }) : t("pick_route.print_needs_code")}
          >
            <span>
              <Button
                size="small"
                icon={<PrinterOutlined />}
                disabled={!entry.location}
                onClick={() => printLabelsAs(buildShelfLabels([entry]).labels, labelFormat)}
              />
            </span>
          </Tooltip>
          <Popconfirm
            title={entry.location ? t("pick_route.confirm_delete_vak", { location: entry.location }) : t("pick_route.confirm_delete", { name: entry.label })}
            okText={t("yes")}
            cancelText={t("no")}
            onConfirm={() => runAction(() => deleteEntry(entry.id, { force: true }))}
          >
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  if (isLoading) return <Loading cover="content" />;

  const moreActions = {
    items: [
      { key: "numbering", icon: <NumberOutlined />, label: t("pick_route.numbering_title"), onClick: () => setNumberingOpen(true) },
      { key: "sortByLocation", icon: <OrderedListOutlined />, label: t("pick_route.sort_by_location"), onClick: applyLocationOrder },
      { key: "split", icon: <SplitCellsOutlined />, label: t("pick_route.split_title"), onClick: () => setSplitOpen(true) },
      { key: "signs", icon: <FlagOutlined />, label: t("pick_route.print_signs"), onClick: () => setSignsOpen(true) },
      { type: "divider" },
      { key: "sync", icon: <ReloadOutlined />, label: t("pick_route.sync"), onClick: handleSync },
      { key: "reset", icon: <ThunderboltOutlined />, label: t("pick_route.reset"), danger: true, onClick: handleReset },
    ],
  };

  const checklist = [
    readiness.withoutLocation > 0 && t("pick_route.check_without_location", { count: readiness.withoutLocation }),
    readiness.needingSplit > 0 && t("pick_route.check_needing_split", { count: readiness.needingSplit }),
    readiness.unplacedArticles > 0 && t("pick_route.check_unplaced_articles", { count: readiness.unplacedArticles }),
    readiness.productsWithoutWeight > 0 && t("pick_route.check_without_weight", { count: readiness.productsWithoutWeight }),
  ].filter(Boolean);

  return (
    <Card
      title={t("pick_route.title")}
      extra={
        <Space wrap>
          {isDirty && (
            <>
              <Button onClick={handleDiscard} disabled={isBusy}>
                {t("pick_route.discard")}
              </Button>
              <Button type="primary" icon={<SaveOutlined />} loading={isBusy} onClick={handleSaveOrder}>
                {t("pick_route.save_order")}
              </Button>
            </>
          )}
          {!isDirty && entries.length > 0 && (
            <>
              <Button type="primary" icon={<AppstoreAddOutlined />} onClick={() => setFillOpen(true)} disabled={isBusy}>
                {t("pick_route.fill_button")}
              </Button>
              <Button icon={<PlusOutlined />} onClick={openCreator}>
                {t("pick_route.add_vak")}
              </Button>
              <Button icon={<BarcodeOutlined />} onClick={() => setLabelRequest({})} disabled={labelPreview.labels.length === 0}>
                {selectedRowKeys.length > 0
                  ? t("pick_route.print_labels_selected", { count: labelPreview.labels.length })
                  : t("pick_route.print_labels")}
              </Button>
              <Dropdown menu={moreActions} trigger={["click"]} disabled={isBusy}>
                <Button>
                  {t("pick_route.more_actions")} <DownOutlined />
                </Button>
              </Dropdown>
            </>
          )}
        </Space>
      }
    >
      <p style={{ maxWidth: 820, color: "#6b6b6b" }}>{t("pick_route.explanation")}</p>

      {!!productsError && (
        <Alert type="error" showIcon className="mb-3" message={t("pick_route.products_error")} description={productsError.message} />
      )}

      {entries.length === 0 && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message={t("pick_route.empty_title")}
          description={t("pick_route.empty_description")}
          action={
            <Space direction="vertical">
              <Button type="primary" loading={isBusy} onClick={handleGenerate}>
                {t("pick_route.generate")}
              </Button>
              <Button onClick={openCreator}>{t("pick_route.add_vak")}</Button>
            </Space>
          }
        />
      )}

      {entries.length > 0 && checklist.length > 0 && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message={t("pick_route.check_title")}
          description={
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              {checklist.map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
          }
          action={
            readiness.needingSplit > 0 || readiness.unplacedArticles > 0 ? (
              <Button icon={<SplitCellsOutlined />} disabled={isDirty} onClick={() => setSplitOpen(true)}>
                {t("pick_route.split_title")}
              </Button>
            ) : null
          }
        />
      )}

      {entries.length > 0 && unplacedProducts.length > 0 && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message={t("pick_route.unplaced_title", { count: unplacedProducts.length })}
          description={t("pick_route.unplaced_description")}
          action={
            <Button loading={isBusy} disabled={isDirty} onClick={handleSync}>
              {t("pick_route.sync")}
            </Button>
          }
        />
      )}

      {packageEntryIds.size > 0 && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message={t("pick_route.package_entries_title", { count: packageEntryIds.size })}
          description={t("pick_route.package_entries_description")}
        />
      )}

      {labelFixes.size > 0 && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message={t("pick_route.label_mismatch_title", { count: labelFixes.size })}
          description={t("pick_route.label_mismatch_description")}
          action={
            <Space direction="vertical">
              <Button
                size="small"
                onClick={() => {
                  setOnlyLabelMismatch(true);
                  setTab(TAB_TABLE);
                }}
                disabled={onlyLabelMismatch && tab === TAB_TABLE}
              >
                {t("pick_route.label_mismatch_show")}
              </Button>
              <Popconfirm
                title={t("pick_route.label_fix_all_confirm", { count: labelFixes.size })}
                okText={t("yes")}
                cancelText={t("no")}
                onConfirm={() => fixLabels([...labelFixes.keys()])}
              >
                <Button size="small" disabled={isDirty || isBusy}>
                  {t("pick_route.label_fix_all")}
                </Button>
              </Popconfirm>
            </Space>
          }
        />
      )}

      {orphanCount > 0 && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message={t("pick_route.orphaned_title", { count: orphanCount })}
          description={t("pick_route.orphaned_description")}
          action={
            <Button disabled={isDirty} onClick={() => setCleanupOpen(true)}>
              {t("pick_route.cleanup")}
            </Button>
          }
        />
      )}

      {isDirty && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message={t("pick_route.unsaved_title")}
          description={t("pick_route.unsaved_description")}
        />
      )}

      <Tabs
        activeKey={tab}
        onChange={setTab}
        items={[
          {
            key: TAB_TABLE,
            label: t("pick_route.tab_table"),
            children: (
              <>
              <Flex justifyContent="between" alignItems="center" className="mb-3">
                <Space wrap>
                  <Input
                    value={searchText}
                    placeholder={t("pick_route.search_placeholder")}
                    prefix={<SearchOutlined />}
                    style={{ width: 280 }}
                    onChange={(event) => setSearchText(event.currentTarget.value)}
                  />
                  <Select
                    allowClear
                    value={zoneFilter}
                    placeholder={t("pick_route.zone_filter")}
                    style={{ width: 180 }}
                    onChange={(value) => setZoneFilter(value || null)}
                    options={zones.map((zone) => ({ value: zone, label: zone }))}
                  />
                  <Checkbox checked={onlyLabelMismatch} onChange={(event) => setOnlyLabelMismatch(event.target.checked)}>
                    {t("pick_route.only_label_mismatch", { count: labelFixes.size })}
                  </Checkbox>
                </Space>
                <Text type="secondary">
                  {t("pick_route.vak_count", { count: readiness.vakken, coded: readiness.vakken - readiness.withoutLocation })}
                </Text>
              </Flex>

              {isFiltered && <Alert type="info" showIcon className="mb-3" message={t("pick_route.filtered_hint")} />}
              {isSorted && !isFiltered && <Alert type="info" showIcon className="mb-3" message={t("pick_route.sorted_hint")} />}

              <div className="table-responsive">
                <Table
                  showSorterTooltip={false}
                  columns={columns}
                  dataSource={filteredDraft}
                  rowKey="id"
                  size="small"
                  rowSelection={{ selectedRowKeys, onChange: setSelectedRowKeys, preserveSelectedRowKeys: true }}
                  onChange={(_pagination, _filters, sorter) => setTableSort(sorter)}
                  pagination={{ defaultPageSize: 50, showSizeChanger: true }}
                  locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t("pick_route.no_entries")} /> }}
                />
              </div>
              </>
            ),
          },
          {
            key: TAB_MAP,
            label: t("pick_route.tab_map"),
            children: <FreezerMap entries={draft} onShowVak={showVakInTable} />,
          },
        ]}
      />

      <Modal
        open={!!linkTarget}
        title={t("pick_route.link_modal_title", { entry: linkTarget?.label })}
        okText={t("pick_route.link_product")}
        cancelText={t("cancel")}
        okButtonProps={{ disabled: !itemToLink, loading: isBusy }}
        onOk={handleLinkProduct}
        onCancel={closeLinkModal}
      >
        <p style={{ color: "#6b6b6b" }}>{t("pick_route.link_modal_description")}</p>
        <Select
          showSearch
          autoFocus
          style={{ width: "100%" }}
          placeholder={t("pick_route.select_product")}
          value={itemToLink}
          searchValue={productSearch}
          onSearch={setProductSearch}
          onChange={setItemToLink}
          filterOption={(input, option) => normalizeProductName(option.label).includes(normalizeProductName(input))}
          options={linkableItems.map(({ product, key, weight, placedAt, allWeights }) => ({
            value: key,
            label: `${product.shopSource} · ${product.name}${weight !== null ? ` · ${weight} gram` : allWeights ? ` · ${t("pick_route.all_weights")}` : ""}${
              placedAt
                ? ` — ${t("pick_route.currently_at", { entry: placedAt.entry.location || placedAt.entry.label, position: placedAt.index + 1 })}`
                : ` — ${t("pick_route.currently_unplaced")}`
            }`,
          }))}
        />
      </Modal>

      <LocationDrawer
        open={drawer.open}
        entry={drawerEntry}
        suggestedCode={drawer.suggestedCode}
        zones={zones}
        saving={isBusy}
        hasNext={!!drawer.entryId && !!nextWithoutCode(drawer.entryId)}
        onClose={closeDrawer}
        onSave={handleSaveVak}
      />

      <FillVakkenModal
        open={fillOpen}
        entries={entries}
        catalogue={catalogue}
        links={links}
        linksLoaded={linksLoaded}
        zones={zones}
        onFill={fillVak}
        onSortRoute={applyLocationOrder}
        onClose={() => setFillOpen(false)}
        describeRefusal={(result) => refusalMessage(t, result)}
      />

      <OrphanCleanupModal
        open={cleanupOpen}
        plan={orphanPlan}
        notLoaded={notLoadedShops}
        busy={isBusy}
        onCancel={() => setCleanupOpen(false)}
        onConfirm={handleCleanup}
      />

      <NumberingModal
        open={numberingOpen}
        entries={draft.filter((entry) => !packageEntryIds.has(entry.id))}
        busy={isBusy}
        onCancel={() => setNumberingOpen(false)}
        onConfirm={handleNumbering}
      />

      <WeightSplitModal
        open={splitOpen}
        entries={entries}
        catalogue={catalogue}
        busy={isBusy}
        onCancel={() => setSplitOpen(false)}
        onConfirm={handleSplit}
      />

      <LabelPrintModal
        open={!!labelRequest}
        labels={labelPreview.labels}
        skipped={labelPreview.skipped}
        format={labelFormat}
        onCancel={() => setLabelRequest(null)}
        onConfirm={startLabelPrint}
      />

      <RackSignModal
        open={signsOpen}
        signs={signPreview.signs}
        skipped={signPreview.skipped}
        selection={selectedRowKeys.length > 0}
        onCancel={() => setSignsOpen(false)}
        onConfirm={() => {
          setSignJob(signPreview.signs);
          setSignsOpen(false);
        }}
      />

      {!!signJob && (
        <div style={{ display: "none" }}>
          <RackSignSheet ref={signSheetRef} signs={signJob} />
        </div>
      )}

      {!!printJob && (
        <div style={{ display: "none" }}>
          <ShelfLabelSheet ref={labelSheetRef} labels={printJob.labels} startSlot={printJob.startSlot} sheet={printJob.sheet} />
        </div>
      )}
    </Card>
  );
};

export default PickRoute;
